import { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "wouter";
import {
  Loader2,
  Link2,
  Trash2,
  AlertCircle,
  Upload,
  FileText,
  X,
  Info,
  CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import Layout, { PageHeader } from "@/components/Layout";
import { Card } from "@/components/ui/Cards";
import { Button } from "@/components/ui/Buttons";
import { Input, Textarea } from "@/components/ui/Inputs";
import NextStepFooter from "@/components/NextStepFooter";
import AiBrainPanel from "@/components/AiBrainPanel";
import { useProject, useDeleteFile } from "@/hooks/useProjects";
import {
  useClientContext,
  useUpdateClientContext,
  useCreateClientLink,
  useDeleteClientLink,
} from "@/hooks/useClientContext";
import { useOrgContext } from "@/hooks/useOrgContext";
import { useFileUploadList } from "@/hooks/useFileUploadList";
import { useUpdateFileTag } from "@/hooks/useFiles";
import { ConfirmDeleteDialog } from "@/components/ConfirmDeleteDialog";
import type { ProjectFile } from "@/lib/api";
import { cn } from "@/lib/utils";

export default function AboutClient() {
  const params = useParams<{ id?: string }>();
  const projectId = params.id;

  const { data: project } = useProject(projectId ?? null);
  const {
    data: clientContextData,
    isLoading,
    refetch: refetchClientContext,
  } = useClientContext(projectId);
  const orgContext = useOrgContext();
  const updateClientContext = useUpdateClientContext(projectId ?? "");
  const createClientLink = useCreateClientLink(projectId ?? "");
  const deleteClientLink = useDeleteClientLink();

  const [isDirty, setIsDirty] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);
  const [autosaveError, setAutosaveError] = useState<string | null>(null);
  const [autosaving, setAutosaving] = useState(false);
  const [formData, setFormData] = useState({
    aboutClient: "",
    theirMarket: "",
    competitors: "",
    additionalNotes: "",
  });

  const [linkUrl, setLinkUrl] = useState("");
  const [isAddingLink, setIsAddingLink] = useState(false);

  // Client-doc upload state
  const {
    items: uploadItems,
    addFiles,
    removeItem: removeUploadItem,
    reset: resetUploads,
  } = useFileUploadList({ kind: "client_doc" });
  const updateFileTag = useUpdateFileTag(projectId ?? "");
  const deleteFile = useDeleteFile(projectId ?? "");
  const [pendingDeleteFile, setPendingDeleteFile] = useState<ProjectFile | null>(null);

  // When an upload finishes, automatically tag it as "reference" and link to project.
  useEffect(() => {
    for (const item of uploadItems) {
      if (item.status === "done" && item.fileId) {
        updateFileTag.mutate({ fileId: item.fileId, tag: "reference" });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadItems.map((i) => `${i.tempId}:${i.status}:${i.fileId ?? ""}`).join("|")]);

  const handleConfirmDeleteFile = async () => {
    if (!pendingDeleteFile) return;
    try {
      await deleteFile.mutateAsync(pendingDeleteFile.id);
      toast.success("File removed.");
      setPendingDeleteFile(null);
    } catch (err) {
      toast.error((err as Error).message ?? "Couldn't remove the file.");
    }
  };

  const clientDocs: ProjectFile[] =
    project?.files?.filter((f: ProjectFile) => f.kind === "client_doc") ?? [];

  // Hydrate the form once on initial load. Don't re-hydrate later — that
  // would clobber unsaved keystrokes if a refetch lands mid-edit.
  useEffect(() => {
    if (clientContextData?.context && !hydrated) {
      const ctx = clientContextData.context;
      setFormData({
        aboutClient: ctx.aboutClient,
        theirMarket: ctx.theirMarket,
        competitors: ctx.competitors,
        additionalNotes: ctx.additionalNotes,
      });
      setIsDirty(false);
      setHydrated(true);
    }
  }, [clientContextData, hydrated]);

  // Autosave: 1.2s debounce after the last change.
  useEffect(() => {
    if (!hydrated || !isDirty) return;
    const t = setTimeout(async () => {
      setAutosaving(true);
      const saved = await updateClientContext.updateContext(formData);
      setAutosaving(false);
      if (saved) {
        setIsDirty(false);
        setLastSavedAt(Date.now());
        setAutosaveError(null);
      } else {
        setAutosaveError(updateClientContext.error ?? "Couldn't save.");
      }
    }, 1200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, isDirty, hydrated]);

  const handleAddLink = async () => {
    if (!linkUrl.trim()) {
      toast.error("Please enter a URL");
      return;
    }

    try {
      setIsAddingLink(true);
      const newLink = await createClientLink.createLink(linkUrl);
      if (newLink) {
        setLinkUrl("");
        toast.success("Link added and fetched");
        // Refetch (instead of full page reload) so unsaved form keystrokes
        // are preserved.
        await refetchClientContext();
      }
    } catch (err) {
      toast.error((err as Error).message ?? "Failed to add link");
    } finally {
      setIsAddingLink(false);
    }
  };

  const handleDeleteLink = async (linkId: string) => {
    try {
      const success = await deleteClientLink.deleteLink(linkId);
      if (success) {
        toast.success("Link deleted");
        await refetchClientContext();
      }
    } catch (err) {
      toast.error((err as Error).message ?? "Failed to delete link");
    }
  };

  if (isLoading) {
    return (
      <Layout projectId={projectId}>
        <PageHeader title="About Client" subtitle="Loading…" />
        <div className="flex items-center justify-center py-24">
          <Loader2 size={20} className="animate-spin text-orange-400" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout projectTitle={project?.title} projectId={projectId}>
      <PageHeader
        title="About Client"
        subtitle="Prospect context, market research, and reference links"
      />

      <div className="p-8 pb-40 max-w-4xl space-y-6">
        {/* Info banner */}
        <Card className="border-orange-400/20 bg-orange-400/5">
          <div className="flex items-start gap-3">
            <div className="text-orange-400 text-lg">ℹ</div>
            <div>
              <p className="text-sm font-medium text-white">
                Info about the client and their market
              </p>
              <p className="text-xs mt-1" style={{ color: "#9CA3AF" }}>
                Use this space to capture what you know about the prospect, their industry, and
                competitive landscape. Add links to their website, news articles, exec interviews,
                and annual reports — we'll pull the text so the AI can reference it when drafting
                sections.
              </p>
            </div>
          </div>
        </Card>

        {/* AI Brain Panel */}
        {projectId && project && (
          <AiBrainPanel
            project={project}
            projectId={projectId}
            clientContext={clientContextData?.context}
            orgContext={orgContext?.data?.context}
          />
        )}

        {/* Form card */}
        <Card>
          <div className="space-y-6">
            {/* About this client */}
            <div>
              <FieldLabel
                label="About this client"
                hint="Who they are, what they do, why they matter to you. Pull from their About page or annual report. Helps the AI write proposals that show you understand their business."
              />
              <Textarea
                value={formData.aboutClient}
                onChange={(e) => {
                  setFormData((prev) => ({
                    ...prev,
                    aboutClient: e.target.value,
                  }));
                  setIsDirty(true);
                }}
                placeholder="Who they are, what they do, why they matter…"
                rows={4}
                className="w-full"
              />
            </div>

            {/* Their market */}
            <div>
              <FieldLabel
                label="Their market"
                hint="The industry / market they operate in. Trends, regulators, customer base, recent shifts. Sources: news articles, sector reports, the client's own strategic docs."
              />
              <Textarea
                value={formData.theirMarket}
                onChange={(e) => {
                  setFormData((prev) => ({
                    ...prev,
                    theirMarket: e.target.value,
                  }));
                  setIsDirty(true);
                }}
                placeholder="The industry / market they operate in. Trends, regulators, customer base."
                rows={3}
                className="w-full"
              />
            </div>

            {/* Competitors */}
            <div>
              <FieldLabel
                label="Competitors"
                hint="Other companies in their space, especially direct rivals on this RFP. Helpful: who else is likely pitching, and how the client tends to evaluate trade-offs between them."
              />
              <Textarea
                value={formData.competitors}
                onChange={(e) => {
                  setFormData((prev) => ({
                    ...prev,
                    competitors: e.target.value,
                  }));
                  setIsDirty(true);
                }}
                placeholder="Other companies in the space, especially direct rivals on this RFP."
                rows={2}
                className="w-full"
              />
            </div>

            {/* Additional notes */}
            <div>
              <FieldLabel
                label="Manual notes"
                hint="Anything else worth knowing — past relationship, internal politics, key personalities, things only humans know. Use this for the soft context the public materials won't capture."
              />
              <Textarea
                value={formData.additionalNotes}
                onChange={(e) => {
                  setFormData((prev) => ({
                    ...prev,
                    additionalNotes: e.target.value,
                  }));
                  setIsDirty(true);
                }}
                placeholder="Anything else worth knowing — past relationship, internal politics, key personalities."
                rows={4}
                className="w-full"
              />
            </div>

            {/* Autosave status — replaces the manual Save button. */}
            <AutosaveStatus
              dirty={isDirty}
              saving={autosaving}
              lastSavedAt={lastSavedAt}
              error={autosaveError}
            />
          </div>
        </Card>

        {/* Linked URLs card */}
        <Card>
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium text-white mb-4">Linked URLs</h3>

              {/* Input + button */}
              <div className="flex gap-2 mb-4">
                <Input
                  type="url"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleAddLink();
                    }
                  }}
                  placeholder="https://example.com"
                  className="flex-1"
                  disabled={isAddingLink}
                />
                <Button
                  onClick={handleAddLink}
                  disabled={isAddingLink || !linkUrl.trim()}
                  className="bg-orange-400 hover:bg-orange-500 disabled:opacity-50"
                >
                  {isAddingLink ? <Loader2 size={16} className="animate-spin" /> : "Add link"}
                </Button>
              </div>

              {/* Links list */}
              {clientContextData?.links && clientContextData.links.length > 0 ? (
                <div className="space-y-2">
                  {clientContextData.links.map((link) => (
                    <div
                      key={link.id}
                      className="flex items-start justify-between p-3 rounded border border-gray-700 bg-gray-800/50 hover:bg-gray-800 transition-colors"
                    >
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <Link2 size={16} className="text-orange-400 mt-0.5 flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-white truncate text-sm">
                            {link.title || link.url}
                          </div>
                          <div className="text-xs text-gray-400 truncate mt-1">{link.url}</div>
                          <div className="mt-2 flex items-center gap-2">
                            {link.fetchStatus === "pending" && (
                              <span className="inline-flex items-center gap-1 text-xs text-gray-400">
                                <Loader2 size={12} className="animate-spin" />
                                Reading…
                              </span>
                            )}
                            {link.fetchStatus === "ready" && (
                              <span className="inline-block px-2 py-1 text-xs font-medium text-green-100 bg-green-900/30 rounded">
                                Read
                              </span>
                            )}
                            {link.fetchStatus === "failed" && (
                              <span className="inline-flex items-center gap-1 text-xs text-red-400">
                                <AlertCircle size={12} />
                                Failed: {link.fetchError}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => handleDeleteLink(link.id)}
                        className="ml-2 p-2 text-gray-400 hover:text-red-400 transition-colors flex-shrink-0"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-gray-400 text-sm">
                  <p>Paste links to the client's website, news articles, exec interviews.</p>
                  <p className="mt-1">We'll pull the text so the AI can use it.</p>
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* Reference documents — multi-file upload */}
        <Card>
          <div className="space-y-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-sm font-semibold text-white">Reference documents</h3>
                <ClientFilesTooltip />
              </div>
              <p className="text-xs" style={{ color: "#6B7280" }}>
                Drop anything that helps the AI understand this client. The more context you
                provide, the sharper the drafts.
              </p>
            </div>

            <ClientDocsUploadZone onAddFiles={addFiles} />

            {/* In-flight uploads */}
            {uploadItems.length > 0 && (
              <div className="space-y-2">
                {uploadItems.map((item) => (
                  <div
                    key={item.tempId}
                    className="flex items-center gap-3 p-3 rounded-lg border"
                    style={{
                      backgroundColor: "rgba(255,255,255,0.02)",
                      borderColor: "rgba(229,231,235,0.06)",
                    }}
                  >
                    <FileText size={14} className="text-orange-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white truncate">{item.filename}</p>
                      <p className="text-xs" style={{ color: "#6B7280" }}>
                        {(item.byteSize / 1024 / 1024).toFixed(1)} MB ·{" "}
                        {item.status === "uploading"
                          ? `Uploading ${item.progressPct}%`
                          : item.status === "done"
                            ? "Done"
                            : item.status === "error"
                              ? `Error: ${item.error ?? "upload failed"}`
                              : "Waiting"}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeUploadItem(item.tempId)}
                      className="p-1.5 rounded text-gray-400 hover:text-red-400 hover:bg-white/5 flex-shrink-0"
                      title="Remove from list"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => resetUploads()}
                  className="text-xs text-gray-400 hover:text-white"
                >
                  Clear list
                </button>
              </div>
            )}

            {/* Already-saved client docs */}
            {clientDocs.length > 0 ? (
              <div className="space-y-2">
                {clientDocs.map((file) => (
                  <div
                    key={file.id}
                    className="flex items-center gap-3 p-3 rounded-lg border"
                    style={{
                      backgroundColor: "rgba(255,255,255,0.02)",
                      borderColor: "rgba(229,231,235,0.06)",
                    }}
                  >
                    <div
                      className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: "rgba(249,115,22,0.1)" }}
                    >
                      <FileText size={14} style={{ color: "#F97316" }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white truncate">{file.filename}</p>
                      <p className="text-xs" style={{ color: "#6B7280" }}>
                        {(file.byteSize / 1024 / 1024).toFixed(1)} MB
                      </p>
                    </div>
                    <span
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium flex-shrink-0"
                      style={{
                        backgroundColor: "rgba(34,197,94,0.12)",
                        color: "#22c55e",
                      }}
                    >
                      <CheckCircle2 size={10} />
                      Saved
                    </span>
                    <button
                      type="button"
                      onClick={() => setPendingDeleteFile(file)}
                      className="p-1.5 rounded text-gray-400 hover:text-red-400 hover:bg-white/5 flex-shrink-0"
                      title="Remove file"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            ) : uploadItems.length === 0 ? (
              <p className="text-xs text-center py-6" style={{ color: "#6B7280" }}>
                No reference documents yet.
              </p>
            ) : null}
          </div>
        </Card>
      </div>

      <ConfirmDeleteDialog
        open={pendingDeleteFile !== null}
        onOpenChange={(o) => {
          if (!o) setPendingDeleteFile(null);
        }}
        title="Remove this file?"
        description={
          pendingDeleteFile
            ? `"${pendingDeleteFile.filename}" will be deleted. The AI will no longer reference it in drafts.`
            : ""
        }
        requiredPhrase="remove"
        confirmLabel="Remove file"
        onConfirm={handleConfirmDeleteFile}
        busy={deleteFile.isPending}
      />

      {projectId && <NextStepFooter currentStep="about-client" projectId={projectId} />}
    </Layout>
  );
}

// ---------------------------------------------------------------------------
// AutosaveStatus — same component as About Us. Inlined here too (no shared
// helpers folder yet) so the two pages stay self-contained.
// ---------------------------------------------------------------------------

function AutosaveStatus({
  dirty,
  saving,
  lastSavedAt,
  error,
}: {
  dirty: boolean;
  saving: boolean;
  lastSavedAt: number | null;
  error: string | null;
}) {
  const [, force] = useState(0);
  useEffect(() => {
    const t = setInterval(() => force((v) => v + 1), 30_000);
    return () => clearInterval(t);
  }, []);

  let text: string;
  let color: string;
  if (error) {
    text = `Save failed — ${error}`;
    color = "#ef4444";
  } else if (saving) {
    text = "Saving…";
    color = "#9CA3AF";
  } else if (dirty) {
    text = "Unsaved changes";
    color = "#EAB308";
  } else if (lastSavedAt) {
    text = `Saved ${formatRelative(lastSavedAt)}`;
    color = "#22c55e";
  } else {
    text = "Changes save automatically.";
    color = "#6B7280";
  }

  return (
    <div className="flex items-center justify-end gap-2 text-xs" style={{ color }}>
      {saving && <Loader2 size={12} className="animate-spin" />}
      {!saving && lastSavedAt && !dirty && !error && <CheckCircle2 size={12} />}
      <span>{text}</span>
    </div>
  );
}

function formatRelative(ts: number): string {
  const sec = Math.floor((Date.now() - ts) / 1000);
  if (sec < 5) return "just now";
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.floor(hr / 24)}d ago`;
}

// ---------------------------------------------------------------------------
// FieldLabel — label + small ⓘ icon with hover/click hint explaining the field.
// ---------------------------------------------------------------------------

function FieldLabel({ label, hint }: { label: string; hint: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="flex items-center gap-2 mb-2">
      <label className="block text-sm font-medium text-white">{label}</label>
      <div className="relative inline-flex">
        <button
          type="button"
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={() => setOpen(false)}
          onFocus={() => setOpen(true)}
          onBlur={() => setOpen(false)}
          onClick={() => setOpen((v) => !v)}
          className="text-gray-400 hover:text-orange-400"
          aria-label={`About ${label}`}
          title={`About ${label}`}
        >
          <Info size={13} />
        </button>
        {open && (
          <div
            className="absolute left-6 top-0 z-50 w-80 rounded-lg p-3 shadow-lg border"
            style={{
              backgroundColor: "#1F2933",
              borderColor: "rgba(229,231,235,0.1)",
            }}
          >
            <p
              className="text-[10px] uppercase font-semibold mb-1.5"
              style={{ color: "#F97316", letterSpacing: "0.05em" }}
            >
              About this field
            </p>
            <p className="text-xs leading-snug" style={{ color: "#D1D5DB" }}>
              {hint}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ClientFilesTooltip — hover/click info icon listing what helps the AI.
// ---------------------------------------------------------------------------

const ABOUT_CLIENT_SUGGESTIONS = [
  "Annual reports / financials",
  "Brand decks / brand books",
  "News articles & press releases",
  "Exec interview transcripts or notes",
  "Strategic plans & public statements",
  "Org chart / leadership bios",
  "Past creative work for them (if any)",
  "Competitor materials for context",
];

function ClientFilesTooltip() {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative inline-flex">
      <button
        type="button"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onClick={() => setOpen((v) => !v)}
        className="text-gray-400 hover:text-orange-400"
        aria-label="What kind of files help?"
        title="What kind of files help the AI?"
      >
        <Info size={14} />
      </button>
      {open && (
        <div
          className="absolute left-6 top-0 z-50 w-72 rounded-lg p-3 shadow-lg border"
          style={{
            backgroundColor: "#1F2933",
            borderColor: "rgba(229,231,235,0.1)",
          }}
        >
          <p
            className="text-[10px] uppercase font-semibold mb-2"
            style={{ color: "#F97316", letterSpacing: "0.05em" }}
          >
            Files that help the AI
          </p>
          <ul className="space-y-1">
            {ABOUT_CLIENT_SUGGESTIONS.map((s, i) => (
              <li key={i} className="text-xs leading-snug" style={{ color: "#D1D5DB" }}>
                — {s}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ClientDocsUploadZone — multi-file drop / click-to-pick.
// ---------------------------------------------------------------------------

function ClientDocsUploadZone({ onAddFiles }: { onAddFiles: (files: File[]) => void }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const handleFiles = useCallback(
    (files: FileList | null) => {
      if (!files || files.length === 0) return;
      onAddFiles(Array.from(files));
    },
    [onAddFiles],
  );

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        handleFiles(e.dataTransfer.files);
      }}
      onClick={() => inputRef.current?.click()}
      className={cn(
        "rounded-xl border-2 border-dashed px-6 py-8 flex flex-col items-center justify-center gap-2 cursor-pointer transition-all",
      )}
      style={{
        borderColor: dragOver ? "rgba(249,115,22,0.5)" : "rgba(229,231,235,0.15)",
        backgroundColor: dragOver ? "rgba(249,115,22,0.05)" : "transparent",
      }}
    >
      <input
        ref={inputRef}
        type="file"
        multiple
        accept="application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/png,image/jpeg"
        onChange={(e) => {
          handleFiles(e.target.files);
          // Reset so the same file can be re-selected later
          if (inputRef.current) inputRef.current.value = "";
        }}
        className="hidden"
      />
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center"
        style={{ backgroundColor: "rgba(249,115,22,0.1)" }}
      >
        <Upload size={18} style={{ color: "#F97316" }} />
      </div>
      <p className="text-sm text-white font-medium">
        Drop PDFs, Word docs, or images — or click to browse
      </p>
      <p className="text-xs" style={{ color: "#6B7280" }}>
        Multiple files OK · max 10 MB each
      </p>
    </div>
  );
}
